import Config from '../constants/Config';

/**
 * Date format utility that supports configurable regional date formats.
 *
 * Config.dateFormat can be:
 *   'DMY' - DD/MM/YYYY (European default)
 *   'MDY' - MM/DD/YYYY (US)
 *   'YMD' - YYYY/MM/DD (ISO-style)
 *
 * Storage format is always YYYY-MM-DD (ISO).
 * Users can type dates with any separator: / - . or space.
 */

const FORMAT = Config.dateFormat || 'DMY';

const placeholders = {
  DMY: 'DD/MM/YYYY',
  MDY: 'MM/DD/YYYY',
  YMD: 'YYYY/MM/DD',
};

/**
 * Returns the placeholder string for the configured date format.
 */
export function getDatePlaceholder() {
  return placeholders[FORMAT] || placeholders.DMY;
}

/**
 * Splits a user-typed date string on any common separator (/ - . space)
 * and returns the numeric parts, or null if not parseable.
 */
function splitParts(input) {
  const trimmed = (input || '').trim();
  if (!trimmed) return null;
  const parts = trimmed.split(/[\/\-.\s]+/);
  if (parts.length !== 3) return null;
  return parts;
}

/**
 * Parses a user-typed date string into ISO YYYY-MM-DD for storage.
 * Accepts any separator (/ - . space).
 * Returns the ISO string, or the raw input if it can't be parsed
 * (so partial typing is preserved).
 */
export function parseInputDate(input) {
  const parts = splitParts(input);
  if (!parts) return input;

  let day, month, year;

  if (FORMAT === 'MDY') {
    [month, day, year] = parts;
  } else if (FORMAT === 'YMD') {
    [year, month, day] = parts;
  } else {
    // DMY (default)
    [day, month, year] = parts;
  }

  const d = parseInt(day, 10);
  const m = parseInt(month, 10);
  let y = parseInt(year, 10);

  if (isNaN(d) || isNaN(m) || isNaN(y)) return input;
  if (m < 1 || m > 12 || d < 1 || d > 31) return input;

  // Support 2-digit years
  if (y < 100) y += 2000;

  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * Formats an ISO YYYY-MM-DD date string into the configured display format.
 * If the input is already in a regional format or not parseable as ISO,
 * returns it unchanged.
 */
export function formatDisplayDate(isoDate) {
  if (!isoDate) return '';
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return isoDate;

  const [, year, month, day] = match;

  if (FORMAT === 'MDY') {
    return `${month}/${day}/${year}`;
  } else if (FORMAT === 'YMD') {
    return `${year}/${month}/${day}`;
  } else {
    // DMY (default)
    return `${day}/${month}/${year}`;
  }
}

/**
 * Handles the full flow for a date TextInput:
 * - Stores the raw typed value while the user is typing
 * - When the input has 3 parts (complete date), normalises to ISO for storage
 *
 * Returns { display, iso } where display is for the TextInput value
 * and iso is for sending to the server.
 */
export function processDateInput(rawInput) {
  const parts = splitParts(rawInput);
  if (parts && parts.length === 3) {
    const iso = parseInputDate(rawInput);
    // If parseInputDate returned a valid ISO date, format for display
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      return { display: formatDisplayDate(iso), iso };
    }
  }
  // Still typing or invalid - return raw input
  return { display: rawInput, iso: rawInput };
}
